import React, { useCallback, useEffect, useState } from 'react';
import {
  iFloorMapLayer,
  iFloorMapLayerInput,
} from 'services/FloorMap/FloorMap.type';
import { FloorMapLayersApi, FloorMapStandRef } from './FloorMapStudio.types';

const btn: React.CSSProperties = {
  border: '1px solid rgba(17,24,39,0.2)',
  background: '#fff',
  color: 'rgba(17,24,39,0.85)',
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
};

const overlay: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 200,
  background: 'rgba(17,24,39,0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const dialog: React.CSSProperties = {
  background: '#fff',
  borderRadius: 10,
  width: 'min(720px, 100%)',
  maxHeight: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
  color: '#1a1a1a',
  fontSize: 12,
};

const input: React.CSSProperties = {
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '5px 8px',
  fontSize: 12,
  fontFamily: 'inherit',
};

const colorInput: React.CSSProperties = {
  width: 28,
  height: 28,
  padding: 0,
  border: 'none',
};

const DEFAULT_COLOR = '#e53935';

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (value: boolean) => void;
  label: string;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={checked ? 'Visible sur le site' : 'Masqué sur le site'}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 36,
        height: 20,
        padding: 0,
        border: 'none',
        borderRadius: 10,
        background: checked ? '#22c55e' : '#d1d5db',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
          transition: 'left 0.15s',
        }}
      />
    </button>
  );
}

interface LayerRowProps {
  layer: iFloorMapLayer;
  stands: FloorMapStandRef[];
  /** Layer holding each stand, to flag stands taken by another layer */
  owners: Record<string, number>;
  disabled: boolean;
  onChange: (id: number, data: iFloorMapLayerInput) => void;
  onDelete: (layer: iFloorMapLayer) => void;
}

function LayerRow({
  layer,
  stands,
  owners,
  disabled,
  onChange,
  onDelete,
}: LayerRowProps): JSX.Element {
  const [name, setName] = useState(layer.name);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => setName(layer.name), [layer.name]);

  const selected = new Set(layer.stand_ids);
  const term = search.trim().toLowerCase();
  const shown = stands.filter(
    (st) =>
      !term ||
      st.label.toLowerCase().includes(term) ||
      st.exhibitor.toLowerCase().includes(term)
  );

  const toggleStand = (id: string): void => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(layer.id, { stand_ids: Array.from(next) });
  };

  return (
    <div style={{ borderBottom: '1px solid #eee', padding: '8px 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="color"
          value={layer.color}
          disabled={disabled}
          onChange={(e) => onChange(layer.id, { color: e.target.value })}
          aria-label="Couleur"
          style={colorInput}
        />
        <input
          style={{ ...input, flex: 1 }}
          value={name}
          disabled={disabled}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            const value = name.trim();
            if (!value) setName(layer.name);
            else if (value !== layer.name) onChange(layer.id, { name: value });
          }}
          aria-label="Nom du calque"
        />
        <span style={{ color: '#666', whiteSpace: 'nowrap' }}>
          {layer.stand_ids.length} stand(s)
        </span>
        <button style={btn} onClick={() => setOpen(!open)}>
          {open ? 'Fermer' : 'Stands'}
        </button>
        <Switch
          checked={layer.is_visible}
          label={`Afficher ${layer.name}`}
          onChange={(value) => onChange(layer.id, { is_visible: value })}
        />
        <button
          style={{ ...btn, color: '#b91c1c' }}
          disabled={disabled}
          onClick={() => onDelete(layer)}
          aria-label="Supprimer"
        >
          🗑
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 8, paddingLeft: 36 }}>
          {stands.length === 0 ? (
            <div style={{ color: '#666' }}>
              Aucun stand sur le plan vectoriel.
            </div>
          ) : (
            <>
              <input
                style={{ ...input, width: '100%', marginBottom: 6 }}
                placeholder="Rechercher un stand ou un exposant"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <div
                style={{
                  maxHeight: 180,
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: 6,
                  padding: 6,
                }}
              >
                {shown.map((st) => {
                  const other = owners[st.id];
                  const taken = other !== undefined && other !== layer.id;
                  return (
                    <label
                      key={st.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '2px 0',
                        opacity: taken ? 0.55 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(st.id)}
                        onChange={() => toggleStand(st.id)}
                      />
                      <b>{st.label || '—'}</b>
                      {st.exhibitor && <span>{st.exhibitor}</span>}
                      {taken && (
                        <span style={{ color: '#888' }}>
                          (autre calque : sera déplacé)
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

interface LayersModalProps {
  api: FloorMapLayersApi;
  stands: FloorMapStandRef[];
  onClose: () => void;
}

export function FloorMapLayersModal({
  api,
  stands,
  onClose,
}: LayersModalProps): JSX.Element {
  const [layers, setLayers] = useState<iFloorMapLayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLOR);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (e: any) {
      setError(e?.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    api
      .list()
      .then(setLayers)
      .catch((e) => setError(e?.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, [api]);

  const owners: Record<string, number> = {};
  layers.forEach((l) => l.stand_ids.forEach((id) => (owners[id] = l.id)));

  const handleCreate = (): void => {
    const name = newName.trim();
    if (!name) return;
    void run(async () => {
      const created = await api.create({ name, color: newColor });
      setLayers((prev) => [...prev, created]);
      setNewName('');
    });
  };

  const handleChange = (id: number, data: iFloorMapLayerInput): void => {
    void run(async () => {
      const updated = await api.update(id, data);
      // Assigning stands moves them out of other layers: reload to stay in sync
      if (data.stand_ids) setLayers(await api.list());
      else setLayers((prev) => prev.map((l) => (l.id === id ? updated : l)));
    });
  };

  const handleDelete = (layer: iFloorMapLayer): void => {
    if (!window.confirm(`Supprimer le calque « ${layer.name} » ?`)) return;
    void run(async () => {
      await api.remove(layer.id);
      setLayers((prev) => prev.filter((l) => l.id !== layer.id));
    });
  };

  return (
    <div
      style={overlay}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div role="dialog" aria-label="Calques du plan" style={dialog}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700 }}>Calques du plan</span>
          <button style={btn} onClick={onClose} aria-label="Fermer">
            ✕
          </button>
        </div>

        <div style={{ overflowY: 'auto', padding: '12px 16px 16px' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={newColor}
              onChange={(e) => setNewColor(e.target.value)}
              aria-label="Couleur"
              style={colorInput}
            />
            <input
              style={{ ...input, flex: 1 }}
              placeholder="Nom du nouveau calque"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            />
            <button
              style={btn}
              disabled={busy || !newName.trim()}
              onClick={handleCreate}
            >
              + Ajouter
            </button>
          </div>

          {error && (
            <div style={{ padding: '8px 0', color: '#b91c1c' }}>{error}</div>
          )}

          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              Chargement…
            </div>
          ) : layers.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#666' }}>
              Aucun calque.
            </div>
          ) : (
            <div style={{ marginTop: 8 }}>
              {layers.map((layer) => (
                <LayerRow
                  key={layer.id}
                  layer={layer}
                  stands={stands}
                  owners={owners}
                  disabled={busy}
                  onChange={handleChange}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
